import React, { useState, useRef, useEffect } from 'react';
import { useClickOutside } from 'hooks';

const UserSwitcher = ({ translations }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useClickOutside(containerRef, () => {
    setIsOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  }, isOpen);

  // Search users when query changes
  useEffect(() => {
    const searchUsers = async () => {
      if (searchQuery.trim().length < 2) {
        setSearchResults([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await fetch(`/admin/users/search?q=${encodeURIComponent(searchQuery)}`);
        if (response.ok) {
          const users = await response.json();
          setSearchResults(users);
        }
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Debounce search
    const timer = setTimeout(searchUsers, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleImpersonate = async (userId) => {
    try {
      const response = await fetch(`/admin/impersonate/${userId}`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
      });

      if (response.ok) {
        // Reload to show impersonated user view
        window.location.reload();
      } else {
        console.error('Failed to impersonate user');
      }
    } catch (error) {
      console.error('Error impersonating user:', error);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={translations.impersonation?.search_placeholder || '🔍 Search users...'}
          className="w-48 px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3366FF] focus:border-transparent"
        />
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (searchResults.length > 0 || isLoading || searchQuery.trim().length >= 2) && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-[100] max-h-80 overflow-y-auto">
          {isLoading && (
            <div className="px-4 py-3 text-sm text-gray-500">
              {translations.impersonation?.searching || 'Searching...'}
            </div>
          )}

          {!isLoading && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
            <div className="px-4 py-3 text-sm text-gray-500">
              {translations.impersonation?.no_results || 'No results found'}
            </div>
          )}

          {!isLoading && searchResults.map((user) => (
            <button
              key={user.id}
              onClick={() => handleImpersonate(user.id)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
            >
              <div className="font-medium text-gray-900">{user.name}</div>
              <div className="text-xs text-gray-500">{user.email}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserSwitcher;
